/**
 * Восстановление пароля по почте — для тех, у кого нет AD: сотрудники филиала Грузии и
 * внешние пользователи кабинета (решение 19.10.2026). Доменный пароль здесь не меняется —
 * федерация AD поднята READ_ONLY, и об этом сказано прямо на форме.
 *
 * Один маршрут на два шага: без `token` — просим прислать ссылку, с `token` из письма —
 * ставим новый пароль. Отдельная страница, а не модалка на логине: по ссылке из письма
 * человек приходит прямо сюда, в том числе с телефона.
 */
import { useMutation } from '@tanstack/react-query'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useEffect, useState } from 'react'
import { Platform } from 'react-native'
import { Input, Text, YStack } from 'tamagui'

import { api } from '../src/api/client'
import { errorText } from '../src/features/edo/errorText'
import { useApp } from '../src/i18n'
import { tone } from '../src/theme/tones'
import { Button } from '../src/ui'

/** Та же нижняя граница, что в политике realm (`length(10)`) и в проверке на бэке. */
const MIN_LENGTH = 10

export default function ResetPasswordScreen() {
  const { t, l } = useApp()
  const router = useRouter()
  const params = useLocalSearchParams<{ token?: string }>()
  // Снимок на первый рендер: дальше токена в адресе уже не будет — мы его оттуда уберём.
  const [token] = useState(() =>
    typeof params.token === 'string' ? params.token : '',
  )

  /**
   * Токен — одноразовый секрет, и в адресной строке ему не место: он остаётся в истории
   * браузера, уезжает в `Referer` на любой внешний ресурс страницы и попадает в стамп
   * адреса, который мониторинг шлёт наружу на каждом событии. На нативе адресной строки
   * нет — там чистить нечего.
   */
  useEffect(() => {
    if (Platform.OS !== 'web' || !token) return
    if (typeof history === 'undefined' || typeof location === 'undefined') return
    history.replaceState(null, '', location.pathname)
  }, [token])

  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [localError, setLocalError] = useState('')

  const request = useMutation({ mutationFn: () => api.passwordResetRequest(login.trim()) })
  const confirm = useMutation({ mutationFn: () => api.passwordResetConfirm(token, password) })

  const save = () => {
    setLocalError('')
    if (password.length < MIN_LENGTH) {
      setLocalError(l('pwreset.tooshort'))
      return
    }
    if (password !== repeat) {
      setLocalError(l('pwreset.mismatch'))
      return
    }
    confirm.mutate()
  }

  const error =
    localError ||
    (confirm.error instanceof Error ? t(errorText(confirm.error)) : '') ||
    (request.error instanceof Error ? t(errorText(request.error)) : '')

  return (
    <YStack flex={1} alignItems="center" justifyContent="center" padding={24} backgroundColor="$bgLayout">
      <YStack width="100%" maxWidth={420} gap={14}>
        <Text fontSize={20} fontWeight="700" color="$color">
          {l(token ? 'pwreset.newtitle' : 'pwreset.title')}
        </Text>

        {token ? (
          confirm.isSuccess ? (
            <YStack gap={12}>
              <Text fontSize={14} color={tone('green').text}>
                {l('pwreset.done')}
              </Text>
              <Button variant="primary" onPress={() => router.replace('/login')}>
                {l('pwreset.tologin')}
              </Button>
            </YStack>
          ) : (
            <YStack gap={10}>
              <Text fontSize={13} color="$muted">
                {l('pwreset.newhint')}
              </Text>
              <Input
                value={password}
                onChangeText={setPassword}
                placeholder={l('pwreset.newtitle')}
                secureTextEntry
                autoCapitalize="none"
                autoComplete="new-password"
              />
              <Input
                value={repeat}
                onChangeText={setRepeat}
                placeholder={l('pwreset.repeat')}
                secureTextEntry
                autoCapitalize="none"
                autoComplete="new-password"
                onSubmitEditing={save}
              />
              <Button
                variant="primary"
                disabled={confirm.isPending}
                opacity={confirm.isPending ? 0.5 : 1}
                onPress={save}
              >
                {l(confirm.isPending ? 'pwreset.saving' : 'pwreset.save')}
              </Button>
            </YStack>
          )
        ) : request.isSuccess ? (
          <YStack gap={12}>
            {/* Текст приходит с сервера и одинаков для любого логина: существует учётка или
                нет — форма не отвечает. */}
            <Text fontSize={14} color="$color">
              {request.data.message}
            </Text>
            <Button onPress={() => router.replace('/login')}>{l('pwreset.tologin')}</Button>
          </YStack>
        ) : (
          <YStack gap={10}>
            <Text fontSize={13} color="$muted">
              {l('pwreset.hint')}
            </Text>
            <Input
              value={login}
              onChangeText={setLogin}
              placeholder={l('login.identifier')}
              autoCapitalize="none"
              autoComplete="username"
              onSubmitEditing={() => login.trim() && request.mutate()}
            />
            <Button
              variant="primary"
              disabled={!login.trim() || request.isPending}
              opacity={!login.trim() || request.isPending ? 0.5 : 1}
              onPress={() => request.mutate()}
            >
              {l(request.isPending ? 'pwreset.sending' : 'pwreset.send')}
            </Button>
            <Text fontSize={12} color="$muted">
              {l('pwreset.domainhint')}
            </Text>
          </YStack>
        )}

        {error ? (
          <Text fontSize={13} color={tone('red').text}>
            {error}
          </Text>
        ) : null}
      </YStack>
    </YStack>
  )
}
